import { AGENT_STATUS_LABELS, type AgentStatus, type Company } from "@station/contracts";
import {
  Alert,
  type BreadcrumbItem,
  EmptyState,
  FormField,
  PageHeader,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Building2, Fuel, Wifi, WifiOff } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { errorMessage } from "../../lib/errors.js";
import { formatDateTime } from "../../lib/format.js";
import { useDocumentTitle } from "../../lib/hooks.js";
import { useTenantShape } from "../../lib/tenant-shape.js";
import { CompanyPicker, companySearchShape, useAccountingCompany } from "../accounting/company.js";
import { useCompanyStations } from "../accounting/shared.js";
import { useStationScope } from "../cashflow/shared.js";
import { AGENT_STATUS_TONE, formatRelativeTime } from "./agent-values.js";
import { useAgentPermissions } from "./queries.js";

/** Ajan ekranlarının ortak arama parametreleri: şirket ve (süzgeç olarak) istasyon. */
export const agentsSearchShape = {
  ...companySearchShape,
  station: z.string().uuid().optional().catch(undefined),
};

export interface AgentsContext {
  companyId: string;
  company: Company;
}

/**
 * Ajan ekranlarının ortak çerçevesi: başlık + ŞİRKET seçici (muhasebe / vardiya ekranlarıyla aynı `?company=`
 * parametresi). İstasyon burada KAPSAM değil SÜZGEÇTİR (özet tüm istasyonları gösterir), bu yüzden seçici
 * sayfanın içinde, süzgeç çubuğunda durur.
 */
export function AgentsPage({
  title,
  description,
  breadcrumb,
  actions,
  children,
}: {
  title: string;
  description?: string;
  /** "Cihazlar ve ajanlar" kökünden SONRAKİ öğeler; verilmezse yalnızca sayfa başlığı eklenir. */
  breadcrumb?: BreadcrumbItem[];
  actions?: ReactNode | ((context: AgentsContext) => ReactNode);
  children: (context: AgentsContext) => ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const accounting = useAccountingCompany();
  const permissions = useAgentPermissions();
  useDocumentTitle(title);
  const context = accounting.company
    ? { companyId: accounting.company.id, company: accounting.company }
    : null;
  return (
    <>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        breadcrumb={[{ label: t("menu.groups.devices") }, ...(breadcrumb ?? [{ label: title }])]}
        actions={
          <>
            <CompanyPicker accounting={accounting} />
            {context && permissions.enabled && permissions.canRead
              ? typeof actions === "function"
                ? actions(context)
                : actions
              : null}
          </>
        }
      />
      {!permissions.enabled ? (
        <ModuleDisabled />
      ) : !permissions.canRead ? (
        <Alert tone="info">{t("agents.noPermission")}</Alert>
      ) : accounting.error ? (
        <Alert tone="danger">{errorMessage(accounting.error)}</Alert>
      ) : accounting.isLoading ? (
        <SkeletonRows rows={6} />
      ) : context === null ? (
        <EmptyState
          headingLevel={2}
          icon={<Building2 />}
          title={t("accounting.company.noneTitle")}
          description={t("accounting.company.noneDescription")}
        />
      ) : (
        // Şirket değişince sayfanın yerel durumu (sekme, süzgeç, seçili ajan) sıfırlanır.
        <div key={context.companyId} className="grid gap-4">
          {children(context)}
        </div>
      )}
    </>
  );
}

/** `STATION_AGENT` kapalı: API 403 `FEATURE_DISABLED` döndüğü için hiç istek atılmaz, boş durum çizilir. */
export function ModuleDisabled(): ReactElement {
  const { t } = useTranslation();
  return (
    <EmptyState
      headingLevel={2}
      icon={<WifiOff />}
      title={t("agents.disabledTitle")}
      description={t("agents.disabledDescription")}
    />
  );
}

export interface AgentStationOption {
  id: string;
  code: string;
  name: string;
}

export interface AgentStationSelection {
  stations: AgentStationOption[];
  /** Süzgeçte seçili istasyon; `null` = tümü. */
  stationId: string | null;
  /** Tek istasyonlu kiracıda ve görünür tek istasyonda seçici çizilmez (arayüz sadeleşir — AGENTS.md §2). */
  showPicker: boolean;
  isLoading: boolean;
  error: unknown;
  select: (stationId: string | null) => void;
}

export function useAgentStations(companyId: string): AgentStationSelection {
  const stations = useCompanyStations(companyId);
  const scope = useStationScope();
  const shape = useTenantShape();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { station?: string };
  const visible = (stations.data ?? []).filter(
    (station) => !scope.restricted || scope.allowedStationIds.includes(station.id),
  );
  const selected = visible.find((station) => station.id === search.station) ?? null;
  return {
    stations: visible,
    stationId: selected?.id ?? null,
    showPicker: visible.length > 1 && !shape.isSingleStation,
    isLoading: stations.isPending,
    error: stations.error,
    select: (stationId) =>
      void navigate({
        to: ".",
        search: ((previous: Record<string, unknown>) => ({
          ...previous,
          station: stationId ?? undefined,
        })) as never,
        replace: true,
      }),
  };
}

export function AgentStationField({
  selection,
  className = "w-64",
}: {
  selection: AgentStationSelection;
  className?: string;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!selection.showPicker) return null;
  return (
    <FormField label={t("agents.fields.station")} className={className}>
      {(control) => (
        <Select
          {...control}
          value={selection.stationId ?? ""}
          onChange={(event) => selection.select(event.target.value || null)}
        >
          <option value="">{t("common.all")}</option>
          {selection.stations.map((station) => (
            <option key={station.id} value={station.id}>
              {station.code} {station.name}
            </option>
          ))}
        </Select>
      )}
    </FormField>
  );
}

/** Hiç istasyon görünmüyorsa (kapsam ya da kurulum eksik) ekranın geri kalanı çizilmez. */
export function NoStations(): ReactElement {
  const { t } = useTranslation();
  return (
    <EmptyState
      headingLevel={2}
      icon={<Fuel />}
      title={t("agents.noStationsTitle")}
      description={t("agents.noStationsDescription")}
    />
  );
}

export function AgentStatusBadge({ status }: { status: AgentStatus }): ReactElement {
  return <StatusBadge tone={AGENT_STATUS_TONE[status]}>{AGENT_STATUS_LABELS[status]}</StatusBadge>;
}

/**
 * Çevrimiçi göstergesi. Anlam YALNIZCA renkle taşınmaz (WCAG 1.4.1): simgenin yanında her zaman metin yazar
 * ve simge `aria-hidden`'dır — ekran okuyucu yalnızca metni okur.
 */
export function OnlineBadge({ online }: { online: boolean }): ReactElement {
  const { t } = useTranslation();
  const Icon = online ? Wifi : WifiOff;
  return (
    <StatusBadge tone={online ? "success" : "warning"}>
      <Icon className="size-3.5" aria-hidden="true" />
      {online ? t("agents.online") : t("agents.offline")}
    </StatusBadge>
  );
}

/** Göreli an + tam tarih (`title`). `<time>` öğesi makine okunur değeri de taşır. */
export function RelativeTime({ value }: { value: string | null | undefined }): ReactElement {
  if (!value) return <span>—</span>;
  return (
    <time dateTime={value} title={formatDateTime(value)}>
      {formatRelativeTime(value)}
    </time>
  );
}

/** Tanım listesi satırı (`<dl>` içinde kullanılır). */
export function Field({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}
